import React from 'react';
import type { LessonScreen as LessonScreenType } from '../../types/course';
import { FillInTheBlankScreen } from './FillInTheBlankScreen';
import { SortOrderScreen } from './SortOrderScreen';
import { MatchPairsScreen } from './MatchPairsScreen';
import { ClassifyScreen } from './ClassifyScreen';
import { RevealStepsScreen } from './RevealStepsScreen';
import { QuickCalcScreen } from './QuickCalcScreen';
import { TapToRevealTakeawaysScreen } from './TapToRevealTakeawaysScreen';

interface Props {
  screen: LessonScreenType;
  onInteractionComplete: () => void;
  completed: boolean;
}

export const LessonScreen: React.FC<Props> = ({ screen, onInteractionComplete, completed }) => {
  // Interactive screen types
  switch (screen.type) {
    case 'fill-in-the-blank':
      return <FillInTheBlankScreen screen={screen} onInteractionComplete={onInteractionComplete} completed={completed} />;
    case 'sort-order':
      return <SortOrderScreen screen={screen} onInteractionComplete={onInteractionComplete} completed={completed} />;
    case 'match-pairs':
      return <MatchPairsScreen screen={screen} onInteractionComplete={onInteractionComplete} completed={completed} />;
    case 'classify':
      return <ClassifyScreen screen={screen} onInteractionComplete={onInteractionComplete} completed={completed} />;
    case 'reveal-steps':
      return <RevealStepsScreen screen={screen} onInteractionComplete={onInteractionComplete} completed={completed} />;
    case 'quick-calc':
      return <QuickCalcScreen screen={screen} onInteractionComplete={onInteractionComplete} completed={completed} />;
    case 'tap-to-reveal-takeaways':
      return <TapToRevealTakeawaysScreen screen={screen} onInteractionComplete={onInteractionComplete} completed={completed} />;
  }

  // Passive screen types
  return (
    <div className="flex flex-col gap-4 px-1">
      {/* Icon + Title */}
      {screen.icon && (
        <div className="text-3xl text-center">{screen.icon}</div>
      )}
      <h3 className="text-lg font-semibold font-serif text-[var(--clue-text-primary)] text-center">
        {screen.title}
      </h3>

      {/* Text screen */}
      {screen.type === 'text' && (
        <p className="text-sm leading-relaxed text-[var(--clue-text-secondary)]">
          {screen.body}
        </p>
      )}

      {/* Bullets screen */}
      {screen.type === 'bullets' && (
        <ul className="space-y-2">
          {screen.bullets.map((bullet, i) => (
            <li key={i} className="flex gap-2 text-sm text-[var(--clue-text-secondary)]">
              <span className="text-[var(--clue-green-accent)] mt-0.5 shrink-0">&#9679;</span>
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Analogy screen */}
      {screen.type === 'analogy' && (
        <div className="rounded-lg bg-[var(--clue-bg-secondary)] border border-[var(--clue-border-mid)] p-4">
          <p className="text-sm leading-relaxed text-[var(--clue-text-secondary)] italic">
            {screen.body}
          </p>
        </div>
      )}

      {/* Comparison screen */}
      {screen.type === 'comparison' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-[var(--clue-bg-secondary)] border border-[var(--clue-border-mid)] p-3">
            <h4 className="text-xs font-semibold text-[var(--clue-green-accent)] mb-2 text-center">
              {screen.comparison.leftLabel}
            </h4>
            <ul className="space-y-1.5">
              {screen.comparison.leftItems.map((item, i) => (
                <li key={i} className="text-xs text-[var(--clue-text-secondary)]">{item}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg bg-[var(--clue-bg-secondary)] border border-[var(--clue-border-mid)] p-3">
            <h4 className="text-xs font-semibold text-[var(--clue-teal-accent)] mb-2 text-center">
              {screen.comparison.rightLabel}
            </h4>
            <ul className="space-y-1.5">
              {screen.comparison.rightItems.map((item, i) => (
                <li key={i} className="text-xs text-[var(--clue-text-secondary)]">{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Key Takeaways screen */}
      {screen.type === 'key-takeaways' && (
        <div className="rounded-lg bg-[var(--clue-green-accent)]/10 border border-[var(--clue-green-accent)]/30 p-4">
          <ul className="space-y-2">
            {screen.bullets.map((bullet, i) => (
              <li key={i} className="flex gap-2 text-sm text-[var(--clue-text-primary)]">
                <span className="text-[var(--clue-green-accent)] shrink-0">&#10003;</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
